'use client';
import React, { useState, useCallback } from 'react';
import { X, MapPin, User, Building2, DollarSign, Zap, FileText, ChevronRight, ChevronLeft, CheckCircle2, Cpu, Upload, Hash, Globe, Layers, Phone, Mail, Tag, Droplets, Wifi, Flame, Trash2, Shield, ScanLine, Sparkles } from 'lucide-react';

interface AddPropertyModalProps {
  onClose: () => void;
  onSuccess: (parcel: NewParcel) => void;
}

export interface NewParcel {
  id: string;
  address: string;
  county: string;
  owner: string;
  area: string;
  status: 'registered';
  taxStatus: 'paid';
  registeredDate: string;
  valuation: string;
  coordinates: string;
  qrCode: string;
  landUse: string;
  zoning: string;
  parcelIdGenerated: string;
}

const COUNTIES = [
  'Montserrado','Nimba','Bong','Lofa','Grand Bassa','Margibi','Maryland',
  'Sinoe','Grand Gedeh','Grand Cape Mount','Gbarpolu','River Gee',
  'Grand Kru','Rivercess','Bomi'
];

const LAND_USE_TYPES = [
  'Residential – Single Family','Residential – Multi-Family','Commercial – Retail',
  'Commercial – Office','Industrial – Light','Industrial – Heavy',
  'Agricultural – Crop','Agricultural – Livestock','Mixed Use',
  'Institutional – Government','Institutional – Religious','Institutional – Educational',
  'Conservation / Protected','Vacant / Undeveloped','Coastal / Waterfront'
];

const ZONING_CODES = [
  'R-1 (Low Density Residential)','R-2 (Medium Density Residential)','R-3 (High Density Residential)',
  'C-1 (Neighborhood Commercial)','C-2 (General Commercial)','C-3 (Central Business District)',
  'I-1 (Light Industrial)','I-2 (Heavy Industrial)','A-1 (Agricultural)',
  'MU-1 (Mixed Use)','OS (Open Space)','PD (Planned Development)','GV (Government)'
];

const OWNERSHIP_TYPES = ['Individual','Corporation','Government Entity','NGO / Non-Profit','Joint Ownership','Trust','Estate'];
const TITLE_TYPES = ['Freehold','Leasehold','Customary','Deed of Conveyance','Certificate of Title','Pending'];

const STEPS = [
  { id: 1, label: 'Parcel Identity', icon: Hash },
  { id: 2, label: 'Location & GIS', icon: MapPin },
  { id: 3, label: 'Land Use & Zoning', icon: Layers },
  { id: 4, label: 'Ownership', icon: User },
  { id: 5, label: 'Valuation', icon: DollarSign },
  { id: 6, label: 'Utilities & Services', icon: Zap },
  { id: 7, label: 'Documents & AI', icon: Cpu },
  { id: 8, label: 'Review & Submit', icon: CheckCircle2 },
];

function generateParcelId(county: string): string {
  const countyMap: Record<string, string> = {
    'Montserrado': 'MON','Nimba': 'NIM','Bong': 'BON','Lofa': 'LOF',
    'Grand Bassa': 'GBS','Margibi': 'MAR','Maryland': 'MRY','Sinoe': 'SIN',
    'Grand Gedeh': 'GGH','Grand Cape Mount': 'GCM','Gbarpolu': 'GBP',
    'River Gee': 'RGE','Grand Kru': 'GKR','Rivercess': 'RCS','Bomi': 'BMI'
  };
  const code = countyMap[county] || 'UNK';
  const num = String(Math.floor(Math.random() * 9000) + 1000);
  return `LR-${code}-${num}`;
}

function generateQRCode(parcelId: string): string {
  const parts = parcelId.split('-');
  return `QR-${parts[1]}-${parts[2]}-${new Date().getFullYear()}`;
}

type UtilityStatus = 'connected' | 'not-connected' | 'partial';

interface FormData {
  // Step 1 – Parcel Identity
  county: string;
  parcelIdGenerated: string;
  qrCode: string;
  registrationDate: string;
  titleType: string;
  titleNumber: string;
  // Step 2 – Location & GIS
  address: string;
  streetNumber: string;
  streetName: string;
  district: string;
  latitude: string;
  longitude: string;
  elevation: string;
  area: string;
  areaUnit: string;
  // Step 3 – Land Use & Zoning
  landUse: string;
  zoningCode: string;
  buildingType: string;
  floors: string;
  yearBuilt: string;
  buildingCondition: string;
  // Step 4 – Ownership
  ownershipType: string;
  ownerName: string;
  ownerNationalId: string;
  ownerPhone: string;
  ownerEmail: string;
  ownerAddress: string;
  coOwners: string;
  // Step 5 – Valuation
  marketValue: string;
  assessedValue: string;
  taxRate: string;
  annualTax: string;
  valuationMethod: string;
  lastValuationDate: string;
  // Step 6 – Utilities
  electricity: UtilityStatus;
  water: UtilityStatus;
  sewage: UtilityStatus;
  internet: UtilityStatus;
  gas: UtilityStatus;
  waste: UtilityStatus;
  // Step 7 – Documents & AI
  aiTags: string[];
  aiConfidence: number;
  aiNotes: string;
  uploadedDocs: string[];
  notes: string;
}

const defaultForm: FormData = {
  county: '', parcelIdGenerated: '', qrCode: '', registrationDate: new Date().toISOString().split('T')[0],
  titleType: '', titleNumber: '',
  address: '', streetNumber: '', streetName: '', district: '', latitude: '', longitude: '',
  elevation: '', area: '', areaUnit: 'ha',
  landUse: '', zoningCode: '', buildingType: '', floors: '', yearBuilt: '', buildingCondition: '',
  ownershipType: '', ownerName: '', ownerNationalId: '', ownerPhone: '', ownerEmail: '',
  ownerAddress: '', coOwners: '',
  marketValue: '', assessedValue: '', taxRate: '0.25', annualTax: '', valuationMethod: 'Self-Assessment (LRA Method 2)',
  lastValuationDate: new Date().toISOString().split('T')[0],
  electricity: 'not-connected', water: 'not-connected', sewage: 'not-connected',
  internet: 'not-connected', gas: 'not-connected', waste: 'not-connected',
  aiTags: [], aiConfidence: 0, aiNotes: '', uploadedDocs: [], notes: '',
};

// LRA Official Property Type → Tax Rate Mapping
const LRA_PROPERTY_TYPE_RATES = [
  // Developed Land
  { key: 'commercial_building', label: 'Commercial Building', rate: 1.5, rateDisplay: '1.5%', group: 'Developed Land' },
  { key: 'industrial_building', label: 'Industrial Building', rate: 1.5, rateDisplay: '1.5%', group: 'Developed Land' },
  { key: 'residential_building', label: 'Residential Building', rate: 0.25, rateDisplay: '0.25%', group: 'Developed Land' },
  { key: 'farm_urban', label: 'Farm in Urban Areas', rate: 0.33, rateDisplay: '0.33%', group: 'Developed Land' },
  { key: 'farm_outside_urban', label: 'Farm outside Urban Areas', rate: 0.25, rateDisplay: '0.25%', group: 'Developed Land' },
  { key: 'mixed_property', label: 'Mixed Property', rate: 1.5, rateDisplay: '1.5%', group: 'Developed Land' },
  { key: 'public_land_commercial', label: 'Building on Public Land – Commercial', rate: 1.0, rateDisplay: '1%', group: 'Developed (Public Land)' },
  { key: 'public_land_residential', label: 'Building on Public Land – Residential', rate: 0.14, rateDisplay: '0.14%', group: 'Developed (Public Land)' },
  // Undeveloped Land
  { key: 'city_town_lots', label: 'City/Town Lots (undeveloped)', rate: 3.5, rateDisplay: '3.5%', group: 'Undeveloped – Within City/Town' },
  { key: 'farmland_undeveloped', label: 'Farmland (undeveloped, within city/town)', rate: 4.0, rateDisplay: '4%', group: 'Undeveloped – Within City/Town' },
  { key: 'vacant_acre_plus', label: 'Acre or Above – Vacant Land (within city/town)', rate: 4.5, rateDisplay: '4.5%', group: 'Undeveloped – Within City/Town' },
  { key: 'outside_city_all_land', label: 'All Land without Building (outside city/town)', rate: 0, rateDisplay: 'LRD $5.00/acre', group: 'Undeveloped – Outside City/Town' },
];

const AI_TAG_SUGGESTIONS = [
  'High-Value Commercial Zone','Flood Risk Area','Heritage Site Proximity',
  'Disputed Boundary','Unregistered Structure Detected','Rapid Development Zone',
  'Agricultural Encroachment','Coastal Erosion Risk','Infrastructure Corridor',
  'Tax Delinquency Risk','Ownership Conflict Likely','Environmental Sensitivity'
];

const utilityConfig: { key: keyof Pick<FormData,'electricity'|'water'|'sewage'|'internet'|'gas'|'waste'>; label: string; icon: React.ElementType }[] = [
  { key: 'electricity', label: 'Electricity', icon: Zap },
  { key: 'water', label: 'Water Supply', icon: Droplets },
  { key: 'sewage', label: 'Sewage / Drainage', icon: Trash2 },
  { key: 'internet', label: 'Internet / Telecom', icon: Wifi },
  { key: 'gas', label: 'Gas / LPG', icon: Flame },
  { key: 'waste', label: 'Waste Collection', icon: Trash2 },
];

const utilityColors: Record<UtilityStatus, { color: string; bg: string; label: string }> = {
  'connected': { color: '#34d399', bg: 'rgba(52,211,153,0.15)', label: 'Connected' },
  'partial': { color: '#fbbf24', bg: 'rgba(251,191,36,0.15)', label: 'Partial' },
  'not-connected': { color: '#f87171', bg: 'rgba(248,113,113,0.15)', label: 'Not Connected' },
};

export default function AddPropertyModal({ onClose, onSuccess }: AddPropertyModalProps) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [aiScanning, setAiScanning] = useState(false);
  const [qrGenerated, setQrGenerated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = useCallback((field: keyof FormData, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCountyChange = (county: string) => {
    const pid = generateParcelId(county);
    const qr = generateQRCode(pid);
    setForm(prev => ({ ...prev, county, parcelIdGenerated: pid, qrCode: qr }));
    setQrGenerated(false);
  };

  const handleGenerateQR = () => {
    if (!form.parcelIdGenerated) return;
    setQrGenerated(true);
  };

  const handleAIScan = () => {
    setAiScanning(true);
    setTimeout(() => {
      const tags = AI_TAG_SUGGESTIONS.sort(() => Math.random() - 0.5).slice(0, 3);
      const confidence = Math.floor(Math.random() * 20) + 78;
      setForm(prev => ({
        ...prev,
        aiTags: tags,
        aiConfidence: confidence,
        aiNotes: `AI analysis detected ${tags[0].toLowerCase()} patterns based on satellite imagery and cadastral data. Confidence score: ${confidence}%. Recommend field verification within 30 days.`
      }));
      setAiScanning(false);
    }, 2200);
  };

  const handleToggleTag = (tag: string) => {
    setForm(prev => ({
      ...prev,
      aiTags: prev.aiTags.includes(tag)
        ? prev.aiTags.filter(t => t !== tag)
        : [...prev.aiTags, tag]
    }));
  };

  const handleAddDoc = () => {
    const docNames = ['Title_Deed.pdf','Survey_Plan.pdf','Ownership_Certificate.pdf','Tax_Clearance.pdf','Building_Permit.pdf'];
    const unused = docNames.filter(d => !form.uploadedDocs.includes(d));
    if (unused.length > 0) {
      setForm(prev => ({ ...prev, uploadedDocs: [...prev.uploadedDocs, unused[0]] }));
    }
  };

  const handleRemoveDoc = (doc: string) => {
    setForm(prev => ({ ...prev, uploadedDocs: prev.uploadedDocs.filter(d => d !== doc) }));
  };

  const calcAnnualTax = () => {
    const assessed = parseFloat(form.assessedValue.replace(/[^0-9.]/g, ''));
    const rate = parseFloat(form.taxRate);
    if (!isNaN(assessed) && !isNaN(rate)) {
      set('annualTax', (assessed * rate / 100).toFixed(2));
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => {
      const newParcel: NewParcel = {
        id: form.parcelIdGenerated,
        address: `${form.streetNumber} ${form.streetName}${form.district ? ', ' + form.district : ''}`,
        county: form.county,
        owner: form.ownerName || 'Unknown',
        area: `${form.area} ${form.areaUnit}`,
        status: 'registered',
        taxStatus: 'paid',
        registeredDate: new Date(form.registrationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        valuation: form.assessedValue ? `$${parseFloat(form.assessedValue.replace(/[^0-9.]/g, '')).toLocaleString()}` : 'Unassessed',
        coordinates: form.latitude && form.longitude ? `${form.latitude}°N ${form.longitude}°W` : 'Not set',
        qrCode: form.qrCode,
        landUse: form.landUse,
        zoning: form.zoningCode,
        parcelIdGenerated: form.parcelIdGenerated,
      };
      setSubmitting(false);
      setSubmitted(true);
      setTimeout(() => {
        onSuccess(newParcel);
        onClose();
      }, 1800);
    }, 1500);
  };

  const canProceed = () => {
    if (step === 1) return !!form.county && !!form.parcelIdGenerated;
    if (step === 2) return !!form.address || (!!form.streetName);
    if (step === 4) return !!form.ownerName;
    return true;
  };

  const inputClass = "w-full px-3 py-2 rounded-lg text-sm outline-none transition-colors";
  const inputStyle = { backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' };
  const labelClass = "block text-xs font-semibold mb-1.5";
  const labelStyle = { color: 'var(--dark-muted)' };
  const sectionTitle = "text-sm font-bold mb-4 flex items-center gap-2";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
      <div
        className="relative w-full max-w-3xl rounded-2xl flex flex-col overflow-hidden"
        style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', maxHeight: '92vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid var(--dark-border)', background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, transparent 60%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <Building2 size={18} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'var(--dark-text)' }}>Register New Property</h2>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Advanced GIS parcel registration with AI-assisted discovery tagging</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" style={{ color: 'var(--dark-muted)' }}>
            <X size={18} />
          </button>
        </div>

        {/* Step Progress */}
        <div className="px-6 py-3 flex-shrink-0 overflow-x-auto" style={{ borderBottom: '1px solid var(--dark-border)', backgroundColor: 'var(--dark-card)' }}>
          <div className="flex items-center gap-1 min-w-max">
            {STEPS.map((s, i) => {
              const StepIcon = s.icon;
              const isActive = step === s.id;
              const isDone = step > s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => isDone && setStep(s.id)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{
                      backgroundColor: isActive ? 'rgba(212,160,23,0.15)' : isDone ? 'rgba(52,211,153,0.08)' : 'transparent',
                      color: isActive ? 'var(--accent)' : isDone ? '#34d399' : 'var(--dark-muted)',
                      border: isActive ? '1px solid rgba(212,160,23,0.3)' : '1px solid transparent',
                      cursor: isDone ? 'pointer' : 'default',
                    }}
                  >
                    {isDone ? <CheckCircle2 size={12} /> : <StepIcon size={12} />}
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{s.id}</span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <ChevronRight size={10} style={{ color: 'var(--dark-border)', flexShrink: 0 }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ── STEP 1: Parcel Identity ── */}
          {step === 1 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <Hash size={15} style={{ color: 'var(--accent)' }} /> Parcel Identity & Registration
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={labelStyle}>County *</label>
                  <select className={inputClass} style={inputStyle} value={form.county} onChange={e => handleCountyChange(e.target.value)}>
                    <option value="">Select county…</option>
                    {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Auto-Generated Parcel ID</label>
                  <div className="flex gap-2">
                    <input readOnly className={inputClass} style={{ ...inputStyle, backgroundColor: 'rgba(212,160,23,0.08)', color: 'var(--accent)', fontFamily: 'monospace', fontWeight: 700 }} value={form.parcelIdGenerated || 'Select county first…'} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Registration Date</label>
                  <input type="date" className={inputClass} style={inputStyle} value={form.registrationDate} onChange={e => set('registrationDate', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Title Type</label>
                  <select className={inputClass} style={inputStyle} value={form.titleType} onChange={e => set('titleType', e.target.value)}>
                    <option value="">Select title type…</option>
                    {TITLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Title / Deed Number</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="e.g. TD-2024-00123" value={form.titleNumber} onChange={e => set('titleNumber', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>QR Code Tag</label>
                  <div className="flex gap-2">
                    <input readOnly className={inputClass} style={{ ...inputStyle, fontFamily: 'monospace', flex: 1 }} value={form.qrCode || '—'} />
                    <button
                      onClick={handleGenerateQR}
                      disabled={!form.parcelIdGenerated}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                      style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)', whiteSpace: 'nowrap' }}
                    >
                      <ScanLine size={13} />
                      {qrGenerated ? 'Regenerate' : 'Generate QR'}
                    </button>
                  </div>
                  {qrGenerated && (
                    <div className="mt-2 flex items-center gap-2 text-xs" style={{ color: '#34d399' }}>
                      <CheckCircle2 size={12} /> QR code generated and linked to parcel
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Location & GIS ── */}
          {step === 2 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <MapPin size={15} style={{ color: 'var(--accent)' }} /> Location & Geo-Coordinates
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={labelStyle}>Street Number</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="e.g. 14" value={form.streetNumber} onChange={e => set('streetNumber', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Street Name *</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="e.g. Broad Street" value={form.streetName} onChange={e => { set('streetName', e.target.value); set('address', `${form.streetNumber} ${e.target.value}`); }} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>District / Zone</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="e.g. Sinkor, Central Monrovia" value={form.district} onChange={e => set('district', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Full Address</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="Auto-filled or enter manually" value={form.address} onChange={e => set('address', e.target.value)} />
                </div>
              </div>
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
                  <Globe size={13} /> GPS / GIS Coordinates
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass} style={labelStyle}>Latitude (°N)</label>
                    <input type="number" step="0.0001" className={inputClass} style={inputStyle} placeholder="e.g. 6.3000" value={form.latitude} onChange={e => set('latitude', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass} style={labelStyle}>Longitude (°W)</label>
                    <input type="number" step="0.0001" className={inputClass} style={inputStyle} placeholder="e.g. 10.7970" value={form.longitude} onChange={e => set('longitude', e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass} style={labelStyle}>Elevation (m)</label>
                    <input type="number" className={inputClass} style={inputStyle} placeholder="e.g. 23" value={form.elevation} onChange={e => set('elevation', e.target.value)} />
                  </div>
                </div>
                {form.latitude && form.longitude && (
                  <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(52,211,153,0.08)', color: '#34d399' }}>
                    <CheckCircle2 size={12} />
                    Coordinates set: {form.latitude}°N {form.longitude}°W
                    {form.elevation && ` · Elevation: ${form.elevation}m`}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={labelStyle}>Land Area</label>
                  <input type="number" step="0.01" className={inputClass} style={inputStyle} placeholder="e.g. 0.34" value={form.area} onChange={e => set('area', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Unit</label>
                  <select className={inputClass} style={inputStyle} value={form.areaUnit} onChange={e => set('areaUnit', e.target.value)}>
                    <option value="ha">Hectares (ha)</option>
                    <option value="m²">Square Meters (m²)</option>
                    <option value="acres">Acres</option>
                    <option value="sq ft">Square Feet</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Land Use & Zoning ── */}
          {step === 3 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <Layers size={15} style={{ color: 'var(--accent)' }} /> Land Use Classification & Zoning
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={labelClass} style={labelStyle}>Land Use Classification</label>
                  <select className={inputClass} style={inputStyle} value={form.landUse} onChange={e => set('landUse', e.target.value)}>
                    <option value="">Select land use…</option>
                    {LAND_USE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass} style={labelStyle}>Zoning Code</label>
                  <select className={inputClass} style={inputStyle} value={form.zoningCode} onChange={e => set('zoningCode', e.target.value)}>
                    <option value="">Select zoning code…</option>
                    {ZONING_CODES.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Building Type</label>
                  <select className={inputClass} style={inputStyle} value={form.buildingType} onChange={e => set('buildingType', e.target.value)}>
                    <option value="">Select type…</option>
                    {['Detached House','Semi-Detached','Apartment Block','Commercial Building','Warehouse','Factory','Office Tower','Mixed-Use Building','Open Land','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Number of Floors</label>
                  <input type="number" min="0" className={inputClass} style={inputStyle} placeholder="e.g. 3" value={form.floors} onChange={e => set('floors', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Year Built</label>
                  <input type="number" min="1900" max={new Date().getFullYear()} className={inputClass} style={inputStyle} placeholder="e.g. 2005" value={form.yearBuilt} onChange={e => set('yearBuilt', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Building Condition</label>
                  <select className={inputClass} style={inputStyle} value={form.buildingCondition} onChange={e => set('buildingCondition', e.target.value)}>
                    <option value="">Select condition…</option>
                    {['Excellent','Good','Fair','Poor','Dilapidated','Under Construction','Demolished'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 4: Ownership ── */}
          {step === 4 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <User size={15} style={{ color: 'var(--accent)' }} /> Ownership Details
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={labelStyle}>Ownership Type</label>
                  <select className={inputClass} style={inputStyle} value={form.ownershipType} onChange={e => set('ownershipType', e.target.value)}>
                    <option value="">Select type…</option>
                    {OWNERSHIP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Owner / Entity Name *</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="Full legal name" value={form.ownerName} onChange={e => set('ownerName', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>National ID / Business Reg.</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="e.g. LR-NID-123456" value={form.ownerNationalId} onChange={e => set('ownerNationalId', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Phone Number</label>
                  <div className="relative">
                    <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                    <input type="tel" className={inputClass} style={{ ...inputStyle, paddingLeft: '2rem' }} placeholder="+231 77 000 0000" value={form.ownerPhone} onChange={e => set('ownerPhone', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Email Address</label>
                  <div className="relative">
                    <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                    <input type="email" className={inputClass} style={{ ...inputStyle, paddingLeft: '2rem' }} placeholder="owner@example.com" value={form.ownerEmail} onChange={e => set('ownerEmail', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Owner Mailing Address</label>
                  <input type="text" className={inputClass} style={inputStyle} placeholder="If different from property" value={form.ownerAddress} onChange={e => set('ownerAddress', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass} style={labelStyle}>Co-Owners / Joint Holders</label>
                  <textarea rows={2} className={inputClass} style={{ ...inputStyle, resize: 'none' }} placeholder="List co-owners separated by semicolons…" value={form.coOwners} onChange={e => set('coOwners', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 5: Valuation ── */}
          {step === 5 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <DollarSign size={15} style={{ color: 'var(--accent)' }} /> Property Valuation — LRA Tax Assessment
              </p>

              {/* LRA Property Type Selector */}
              <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.25)' }}>
                <p className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
                  <Tag size={13} /> LRA Property Classification & Tax Rate
                </p>
                <div className="sm:col-span-2 mb-3">
                  <label className={labelClass} style={labelStyle}>LRA Property Type *</label>
                  <select
                    className={inputClass}
                    style={inputStyle}
                    value={form.taxRate}
                    onChange={e => {
                      const selected = LRA_PROPERTY_TYPE_RATES.find(t => String(t.rate) === e.target.value);
                      if (selected) {
                        set('taxRate', String(selected.rate));
                        const assessed = parseFloat(form.assessedValue.replace(/[^0-9.]/g, ''));
                        if (!isNaN(assessed) && assessed > 0 && selected.rate > 0) {
                          set('annualTax', (assessed * selected.rate / 100).toFixed(2));
                        }
                      }
                    }}
                  >
                    {['Developed Land', 'Developed (Public Land)', 'Undeveloped – Within City/Town', 'Undeveloped – Outside City/Town'].map(group => (
                      <optgroup key={group} label={group}>
                        {LRA_PROPERTY_TYPE_RATES.filter(t => t.group === group).map(t => (
                          <option key={t.key} value={String(t.rate)}>{t.label} — {t.rateDisplay}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                {(() => {
                  const selected = LRA_PROPERTY_TYPE_RATES.find(t => String(t.rate) === form.taxRate);
                  return selected ? (
                    <div className="flex items-center gap-3 text-xs">
                      <span className="px-2 py-1 rounded-lg font-bold font-mono-data" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
                        {selected.rateDisplay}
                      </span>
                      <span style={{ color: 'var(--dark-muted)' }}>{selected.group}</span>
                    </div>
                  ) : null;
                })()}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} style={labelStyle}>Declared Market Value (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: 'var(--dark-muted)' }}>$</span>
                    <input type="number" className={inputClass} style={{ ...inputStyle, paddingLeft: '1.5rem' }} placeholder="0.00" value={form.marketValue} onChange={e => set('marketValue', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Assessed Value (USD)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: 'var(--dark-muted)' }}>$</span>
                    <input type="number" className={inputClass} style={{ ...inputStyle, paddingLeft: '1.5rem' }} placeholder="0.00" value={form.assessedValue} onChange={e => set('assessedValue', e.target.value)} onBlur={calcAnnualTax} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>LRA Tax Rate (%)</label>
                  <input readOnly className={inputClass} style={{ ...inputStyle, color: 'var(--accent)', fontWeight: 700 }} value={form.taxRate ? `${form.taxRate}%` : '—'} />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Annual Tax Due (Auto-Calculated)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold" style={{ color: 'var(--accent)' }}>$</span>
                    <input readOnly className={inputClass} style={{ ...inputStyle, paddingLeft: '1.5rem', color: 'var(--accent)', fontWeight: 700 }} value={form.annualTax || '—'} />
                  </div>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>LRA Valuation Method</label>
                  <select className={inputClass} style={inputStyle} value={form.valuationMethod} onChange={e => set('valuationMethod', e.target.value)}>
                    {[
                      'Self-Assessment (LRA Method 2)',
                      'Certified Assessor (LRA Approved)',
                      'Professional Firm Valuation (>2,500 sq ft)',
                      'Farmland Valuation (LRA Method 3)',
                      'Field Assessment',
                      'Automated AI Valuation',
                    ].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Last Valuation Date</label>
                  <input type="date" className={inputClass} style={inputStyle} value={form.lastValuationDate} onChange={e => set('lastValuationDate', e.target.value)} />
                </div>
              </div>
              {form.marketValue && form.assessedValue && (
                <div className="rounded-xl p-4" style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)' }}>
                  <p className="text-xs font-bold mb-2" style={{ color: 'var(--accent)' }}>LRA Tax Summary</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    {[
                      { label: 'Declared Market Value', value: `$${parseFloat(form.marketValue).toLocaleString()}` },
                      { label: 'Assessed Value', value: `$${parseFloat(form.assessedValue).toLocaleString()}` },
                      { label: 'Annual Tax Due', value: form.annualTax ? `$${parseFloat(form.annualTax).toLocaleString()}` : 'LRD $5/acre' },
                    ].map(item => (
                      <div key={item.label} className="rounded-lg p-2" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                        <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item.label}</p>
                        <p className="text-sm font-bold font-mono-data" style={{ color: 'var(--dark-text)' }}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs mt-2 text-center" style={{ color: 'var(--dark-muted)' }}>
                    Formula: Declared Market Value × {form.taxRate}% = Annual Tax Due · Due July 1st
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── STEP 6: Utilities ── */}
          {step === 6 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <Zap size={15} style={{ color: 'var(--accent)' }} /> Utility Connections & Services
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {utilityConfig.map(({ key, label, icon: UtilIcon }) => {
                  const current = form[key] as UtilityStatus;
                  const UtilIconComponent = UtilIcon as React.ElementType;
                  return (
                    <div key={key} className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <UtilIconComponent size={14} style={{ color: 'var(--accent)' }} />
                          <span className="text-xs font-semibold" style={{ color: 'var(--dark-text)' }}>{label}</span>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: utilityColors[current].bg, color: utilityColors[current].color }}>
                          {utilityColors[current].label}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        {(['connected','partial','not-connected'] as UtilityStatus[]).map(status => (
                          <button
                            key={status}
                            onClick={() => set(key, status)}
                            className="flex-1 py-1.5 rounded-lg text-xs font-medium transition-all"
                            style={{
                              backgroundColor: current === status ? utilityColors[status].bg : 'var(--dark-elevated)',
                              color: current === status ? utilityColors[status].color : 'var(--dark-muted)',
                              border: current === status ? `1px solid ${utilityColors[status].color}40` : '1px solid transparent',
                            }}
                          >
                            {status === 'connected' ? '✓ Yes' : status === 'partial' ? '~ Partial' : '✗ No'}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP 7: Documents & AI ── */}
          {step === 7 && (
            <div className="space-y-5">
              <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                <Cpu size={15} style={{ color: 'var(--accent)' }} /> Documents & AI Discovery Tagging
              </p>

              {/* Document Upload */}
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: 'var(--dark-text)' }}>
                  <FileText size={13} style={{ color: 'var(--accent)' }} /> Supporting Documents
                </p>
                <div className="space-y-2 mb-3">
                  {form.uploadedDocs.length === 0 && (
                    <p className="text-xs text-center py-4" style={{ color: 'var(--dark-muted)' }}>No documents attached yet</p>
                  )}
                  {form.uploadedDocs.map(doc => (
                    <div key={doc} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                      <div className="flex items-center gap-2">
                        <FileText size={12} style={{ color: 'var(--accent)' }} />
                        <span className="text-xs" style={{ color: 'var(--dark-text)' }}>{doc}</span>
                      </div>
                      <button onClick={() => handleRemoveDoc(doc)} className="p-1 rounded hover:bg-white/10" style={{ color: '#f87171' }}>
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAddDoc}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80 w-full justify-center"
                  style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)', border: '1px dashed rgba(212,160,23,0.4)' }}
                >
                  <Upload size={13} /> Attach Document
                </button>
              </div>

              {/* AI Discovery Tagging */}
              <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid rgba(212,160,23,0.2)' }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold flex items-center gap-1.5" style={{ color: 'var(--dark-text)' }}>
                    <Sparkles size={13} style={{ color: 'var(--accent)' }} /> AI-Assisted Discovery Tagging
                  </p>
                  <button
                    onClick={handleAIScan}
                    disabled={aiScanning}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-50"
                    style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}
                  >
                    {aiScanning ? (
                      <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" /> Scanning…</>
                    ) : (
                      <><Cpu size={12} /> Run AI Scan</>
                    )}
                  </button>
                </div>

                {form.aiConfidence > 0 && (
                  <div className="mb-3 px-3 py-2 rounded-lg flex items-center gap-2" style={{ backgroundColor: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.2)' }}>
                    <CheckCircle2 size={12} style={{ color: '#34d399' }} />
                    <span className="text-xs" style={{ color: '#34d399' }}>AI scan complete · Confidence: {form.aiConfidence}%</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-3">
                  {AI_TAG_SUGGESTIONS.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleToggleTag(tag)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                      style={{
                        backgroundColor: form.aiTags.includes(tag) ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                        color: form.aiTags.includes(tag) ? 'var(--accent)' : 'var(--dark-muted)',
                        border: form.aiTags.includes(tag) ? '1px solid rgba(212,160,23,0.4)' : '1px solid var(--dark-border)',
                      }}
                    >
                      <Tag size={10} />
                      {tag}
                    </button>
                  ))}
                </div>

                {form.aiNotes && (
                  <div className="px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-muted)', lineHeight: '1.6' }}>
                    {form.aiNotes}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className={labelClass} style={labelStyle}>Additional Notes / Remarks</label>
                <textarea rows={3} className={inputClass} style={{ ...inputStyle, resize: 'none' }} placeholder="Any additional observations, field notes, or remarks…" value={form.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </div>
          )}

          {/* ── STEP 8: Review & Submit ── */}
          {step === 8 && (
            <div className="space-y-4">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(52,211,153,0.15)', border: '2px solid #34d399' }}>
                    <CheckCircle2 size={32} style={{ color: '#34d399' }} />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-bold mb-1" style={{ color: '#34d399' }}>Property Registered Successfully</p>
                    <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Parcel {form.parcelIdGenerated} has been added to the GIS registry</p>
                  </div>
                </div>
              ) : (
                <>
                  <p className={sectionTitle} style={{ color: 'var(--dark-text)' }}>
                    <CheckCircle2 size={15} style={{ color: 'var(--accent)' }} /> Review & Confirm Registration
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { label: 'Parcel ID', value: form.parcelIdGenerated, highlight: true },
                      { label: 'QR Code', value: form.qrCode },
                      { label: 'County', value: form.county },
                      { label: 'Address', value: `${form.streetNumber} ${form.streetName}${form.district ? ', ' + form.district : ''}` || '—' },
                      { label: 'Coordinates', value: form.latitude && form.longitude ? `${form.latitude}°N ${form.longitude}°W` : 'Not set' },
                      { label: 'Land Use', value: form.landUse || '—' },
                      { label: 'Zoning', value: form.zoningCode || '—' },
                      { label: 'Owner', value: form.ownerName || '—' },
                      { label: 'Ownership Type', value: form.ownershipType || '—' },
                      { label: 'Market Value', value: form.marketValue ? `$${parseFloat(form.marketValue).toLocaleString()}` : '—' },
                      { label: 'Assessed Value', value: form.assessedValue ? `$${parseFloat(form.assessedValue).toLocaleString()}` : '—' },
                      { label: 'Annual Tax', value: form.annualTax ? `$${parseFloat(form.annualTax).toLocaleString()}` : '—' },
                      { label: 'Title Type', value: form.titleType || '—' },
                      { label: 'Area', value: form.area ? `${form.area} ${form.areaUnit}` : '—' },
                    ].map(item => (
                      <div key={item.label} className="flex items-start justify-between px-3 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item.label}</span>
                        <span className="text-xs font-semibold text-right ml-2" style={{ color: item.highlight ? 'var(--accent)' : 'var(--dark-text)', fontFamily: item.highlight ? 'monospace' : 'inherit' }}>
                          {item.value || '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                  {form.aiTags.length > 0 && (
                    <div className="px-3 py-3 rounded-lg" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>AI Discovery Tags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {form.aiTags.map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {form.uploadedDocs.length > 0 && (
                    <div className="px-3 py-3 rounded-lg" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Attached Documents ({form.uploadedDocs.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {form.uploadedDocs.map(doc => (
                          <span key={doc} className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1" style={{ backgroundColor: 'rgba(52,211,153,0.1)', color: '#34d399' }}>
                            <FileText size={10} />{doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: 'var(--dark-card)' }}>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Step {step} of {STEPS.length}</span>
              <div className="flex gap-1">
                {STEPS.map(s => (
                  <div key={s.id} className="h-1 rounded-full transition-all" style={{ width: step >= s.id ? '16px' : '6px', backgroundColor: step > s.id ? '#34d399' : step === s.id ? 'var(--accent)' : 'var(--dark-border)' }} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {step > 1 && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-80"
                  style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }}
                >
                  <ChevronLeft size={13} /> Back
                </button>
              )}
              {step < STEPS.length ? (
                <button
                  onClick={() => setStep(s => s + 1)}
                  disabled={!canProceed()}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}
                >
                  Next <ChevronRight size={13} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-80 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}
                >
                  {submitting ? (
                    <><span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin inline-block" /> Registering…</>
                  ) : (
                    <><Shield size={13} /> Register Property</>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
